<template>
  <div class="demo-crud-char2">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="demo-crud-char2-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${registros.length} registros`"
          @new="addRegistro"
          @refresh="loadRegistros"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando registros...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div class="alert alert-info" v-if="!loading && !error && (!registros || registros.length === 0)">
          No hay registros. Haga clic en 'Nuevo' para agregar uno.
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && registros && registros.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="registros"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'DemoChar', dir: 'asc' }"
            filter-placeholder="Buscar registros..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot personalizado para la columna EsActivo -->
            <template #cell-EsActivo="{ value }">
              <span v-if="value" class="badge bg-success">Sí</span>
              <span v-else class="badge bg-secondary">No</span>
            </template>
            
            <!-- Slot personalizado para la fecha -->
            <template #cell-FechaInserta="{ value }">
              <span v-if="value">{{ formatDate(value) }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <!-- Slot personalizado para el peso -->
            <template #cell-Peso="{ value }">
              <span v-if="value !== null && value !== undefined">{{ formatNumber(value) }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editRegistro(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar registro"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.DemoChar, row.EmpresaId)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar registro"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Registro -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="registroModal" 
      tabindex="-1" 
      aria-labelledby="registroModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="registroModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Registro' : 'Nuevo Registro' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <DemoCrudChar2Form
              :registro="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import DemoCrudChar2Service from '../controllers/DemoCrudChar2Controller';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import DemoCrudChar2Form from '../components/DemoCrudChar2Form.vue';

export default {
  name: 'DemoCrudChar2View',
  components: {
    MyDataGrid,
    ToolbarComponent,
    DemoCrudChar2Form
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new DemoCrudChar2Service(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        DemoChar: '',
        EmpresaId: 1,
        Nombre: '',
        Email: '',
        EsActivo: true,
        FechaInserta: '',
        Peso: null
      }
    };
  },
  computed: {
    registros() {
      return this.service.registros;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'DemoChar', label: 'Código', sortable: true },
        { key: 'EmpresaId', label: 'ID Empresa', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'Email', label: 'Email', sortable: true },
        { 
          key: 'EsActivo', 
          label: 'Activo', 
          sortable: true,
          format: (value) => value ? 'Sí' : 'No'
        },
        { key: 'FechaInserta', label: 'Fecha Inserción', sortable: true },
        { key: 'Peso', label: 'Peso', sortable: true }
      ];
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadRegistros() {
      console.log('Cargando registros...');
      await this.service.listar();
    },
    addRegistro() {
      this.isEditing = false;
      this.openModal();
    },
    async editRegistro(registro) {
      this.isEditing = true;
      try {
        // Cargar datos completos del registro
        const response = await this.service.lectura(registro.DemoChar, registro.EmpresaId);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...registro };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar registro:', error);
        this.$alert.error('No se pudo cargar el registro para edición.');
      }
    },
    confirmDelete(demoChar, empresaId) {
      console.log('Iniciando confirmación de eliminación para:', demoChar, empresaId);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el registro con código "${demoChar}" y empresa "${empresaId}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteRegistro(demoChar, empresaId);
        }
      });
    },
    async deleteRegistro(demoChar, empresaId) {
      console.log('Ejecutando eliminación para:', demoChar, empresaId);
      
      try {
        const response = await this.service.borrar(demoChar, empresaId);
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Registro eliminado exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar el registro. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar registro:', error);
        this.$alert.error('No se pudo eliminar el registro. Por favor, intente nuevamente.');
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        DemoChar: '',
        EmpresaId: 1,
        Nombre: '',
        Email: '',
        EsActivo: true,
        FechaInserta: '',
        Peso: null
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        const response = await this.service.guardar(formData);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Registro guardado exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar el registro. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar registro:', error);
        this.$alert.error('No se pudo guardar el registro. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    },
    // Métodos de formateo
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      return parseFloat(value).toFixed(2);
    }
  }
}
</script>

<style scoped>
.demo-crud-char2 {
  min-height: calc(100vh - 200px);
}

.demo-crud-char2-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
